<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted, computed, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import type { IAoeCart, IAoeCartItem } from '@/types/CartTypes'

import AoeCartDetailSelectFreeProduct from '../AoeCartDetailSelectFreeProduct.vue'
import AoeCartDetailFreeProduct from '../AoeCartDetailFreeProduct.vue'
import AoeCartDetailSelectPercentageOffItem from '../AoeCartDetailSelectPercentageOffItem.vue'
import AoeCartDetailCouponAward from '../AoeCartDetailCouponAward.vue'
import AoeCartDetailDiscountedSkuFromList from '../AoeCartDetailDiscountedSkuFromList.vue'
import { SvgIcon } from '@progress/kendo-vue-common'
import { checkOutlineIcon } from '@progress/kendo-svg-icons'
import AoeCartDetailFreeDollars from '../AoeCartDetailFreeDollars.vue'
import AoeCartDetailDiscountedItem from '../AoeCartDetailDiscountedItem.vue'

const logger = useLogger()

const emit = defineEmits(['update-cart'])

const props = defineProps<{
  cart: IAoeCart | null
  productimageurls: Record<string, string>
}>()

const cartItemsByPoeDiscPlanLevelDiscIdDictionary = ref<{
  [id: string]: Array<IAoeCartItem>
}>({})

// Filter gamifications to only show progress bars for unearned discounts
const unearnedGamifications = computed(() => {
  if (!props.cart?.gamification || props.cart.gamification.length === 0) {
    return []
  }

  const seenLevelIds = new Set<number>()
  const deduplicatedGamification = props.cart.gamification
    .filter(gm => {
      if (seenLevelIds.has(gm.poeDiscPlanLevelId)) return false
      seenLevelIds.add(gm.poeDiscPlanLevelId)
      return true
    })
    .map(gm => ({
      ...gm,
      awards: gm.awards
        ? gm.awards.filter((award, index, arr) =>
            arr.findIndex(a => a.poeDiscPlanLevelDiscId === award.poeDiscPlanLevelDiscId) === index
          )
        : [],
    }))

  return deduplicatedGamification.filter(gm => {
    if (!gm.requirements || gm.requirements.length === 0) return false

    const isProgressType = gm.requirements[0].systemKeyword === 'OrderTotal' ||
                          gm.requirements[0].systemKeyword === 'PartyOrderTotal'

    if (!isProgressType) return false

    // Check if this specific gamification has been earned by matching poeDiscPlanLevelDiscId
    // Each award now has a poeDiscPlanLevelDiscId that corresponds to the earned discount
    // This ensures that when there are multiple discounts with the same systemKeyword
    // and awardDescription (e.g., multiple FreeDollars at different thresholds),
    // only the specific earned one's progress bar disappears, not all of them
    if (!props.cart?.earnedDiscounts || props.cart.earnedDiscounts.length === 0) {
      return true // No earned discounts yet, show all progress bars
    }

    const isEarned = gm.awards.some(award =>
      props.cart!.earnedDiscounts!.some(ed =>
        ed.poeDiscPlanLevelDiscId === award.poeDiscPlanLevelDiscId
      )
    )

    // Only show progress bar if not yet earned
    return !isEarned
  })
})

onMounted(async () => {
  logger.debug('onMounted AoeDiscountControl')
  UpdateCartItemsByPoeDiscPlanLevelDiscIdDictionary()
})

watch(
  () => props.cart,
  () => {
    UpdateCartItemsByPoeDiscPlanLevelDiscIdDictionary()
  },
)

function GetCartItemsByPoeDiscPlanLevelDiscId(
  poeDiscPlanLevelDiscId: number,
): IAoeCartItem[] {
  const poeDiscPlanLevelDiscIdKey = GetPoeDiscPlanLevelDiscIdKey(
    poeDiscPlanLevelDiscId,
  )
  const cartItems =
    cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[poeDiscPlanLevelDiscIdKey]
  return cartItems
}

function GetPoeDiscPlanLevelDiscIdKey(poeDiscPlanLevelDiscId: number): string {
  return 'PoeDiscPlanLevelDiscId' + poeDiscPlanLevelDiscId
}

function UpdateCartItemsByPoeDiscPlanLevelDiscIdDictionary() {
  cartItemsByPoeDiscPlanLevelDiscIdDictionary.value = {}
  if (props.cart && props.cart.items) {
    props.cart.items.forEach(item => {
      if (item.poeDiscPlanLevelDiscIds) {
        item.poeDiscPlanLevelDiscIds.forEach(poeDiscPlanLevelDiscId => {
          const poeDiscPlanLevelDiscIdKey = GetPoeDiscPlanLevelDiscIdKey(
            poeDiscPlanLevelDiscId,
          )
          if (
            !cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[
              poeDiscPlanLevelDiscIdKey
            ]
          ) {
            cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[
              poeDiscPlanLevelDiscIdKey
            ] = []
          }
          cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[
            poeDiscPlanLevelDiscIdKey
          ].push(item)
        })
      }
    })
  }
}

function FindCartItems(poeDiscPlanLevelDiscId: number): IAoeCartItem[] {
  const poeDiscPlanLevelDiscIdKey = GetPoeDiscPlanLevelDiscIdKey(
    poeDiscPlanLevelDiscId,
  )
  const cartItems =
    cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[poeDiscPlanLevelDiscIdKey]

  if (cartItems == null) return []

  return cartItems
}

function FindCartItem(
  poeDiscPlanLevelDiscId: number,
  skuCode: string,
): IAoeCartItem | null {
  if (
    Object.keys(cartItemsByPoeDiscPlanLevelDiscIdDictionary.value).length == 0
  )
    return null

  const cartItems = FindCartItems(poeDiscPlanLevelDiscId)
  if (cartItems.length > 0) {
    const cartItem = cartItems.find(item => item.skuCode === skuCode)
    if (cartItem) return cartItem
  }
  return null
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    props.productimageurls,
  )
  const productImageUrl = props.productimageurls[productCode]
  if (productImageUrl) return productImageUrl
  return '#'
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler AoeDiscountControl', event)
  emit('update-cart', event)
}

function FilterEarnedDisounts(earnedDiscounts: IAoeCart['earnedDiscounts']) {
  return earnedDiscounts.filter(
    ed =>
      (ed.systemKeyword !== 'FreeDollarsOffOrder' &&
        ed.systemKeyword !== 'Coupon') ||
      (ed.systemKeyword === 'Coupon' &&
        (ed.freeDollarsCost == null || ed.freeDollarsCost == 0)),
  )
}
</script>

<template>
  <div
    v-if="props.cart != null && props.cart.aoeCartIdToken != ''"
    class="aoe-discount-control"
  >
    <div class="row party-order-entry-section-header-row">
      <div
        class="col-12 party-order-entry-section-header-div discount-section-header"
      >
        {{ t('partyOrderEntry.DiscountSectionHeader') }}
      </div>
    </div>
    <div
      v-if="unearnedGamifications.length > 0"
      class="aoe-discount-gamification"
    >
      <div
        class="row mt-3"
        v-for="gm in unearnedGamifications"
        :key="gm.differenceMessage"
      >
        <div class="row mt-3 gamificationDiv">
          <div
            class="gamificationMessage col-12"
            v-html="gm.differenceMessage"
          ></div>
        </div>
        <div class="row gamificationSliderDiv">
          <div class="col-12">
            <input
              class="slider gamificationRange"
              disabled
              type="range"
              min="0"
              :max="gm.requirements[0].totalAmount"
              :value="
                gm.requirements[0].totalAmount - gm.requirements[0].difference
              "
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="
        unearnedGamifications.length > 0 &&
        props.cart.earnedDiscounts != undefined &&
        props.cart.earnedDiscounts.length > 0
      "
      class="row mt-3"
    >
      <div class="col-12 discount-divider">
        <hr />
      </div>
    </div>
    <div
      class="aoe-cart-detail-discount pb-2 pt-2"
      v-if="
        props.cart.earnedDiscounts != undefined &&
        props.cart.earnedDiscounts.length > 0
      "
    >
      <div
        class="row mt-3"
        v-for="(earnedDiscount, index) in FilterEarnedDisounts(
          props.cart.earnedDiscounts,
        )"
        :key="earnedDiscount.poeDiscPlanLevelDiscId"
      >
        <template v-if="earnedDiscount.systemKeyword === 'SelectFreeProduct'">
          <AoeCartDetailSelectFreeProduct
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :GetCartItemsByPoeDiscPlanLevelDiscId="
              GetCartItemsByPoeDiscPlanLevelDiscId
            "
            :FindCartItem="FindCartItem"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
            @update-cart="updateCartHandler($event)"
          ></AoeCartDetailSelectFreeProduct>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'FreeProduct'">
          <AoeCartDetailFreeProduct
            :earnedDiscount="earnedDiscount"
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
          ></AoeCartDetailFreeProduct>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'FreeShipping'">
          <div class="col-2 col-md-1">
            <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
            </SvgIcon>
          </div>
          <div class="col-10 col-md-9 free-shipping-award-desc">
            {{ earnedDiscount.awardDescription }}
          </div>
        </template>
        <template
          v-else-if="earnedDiscount.systemKeyword === 'PercentageOffOrder'"
        >
          <div class="col-2 col-md-1">
            <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
            </SvgIcon>
          </div>
          <div class="col-10 col-md-9 percent-off-order-award-desc">
            {{ earnedDiscount.awardMessage }}&nbsp;{{
              earnedDiscount.awardDescription
            }}
          </div>
        </template>
        <template
          v-else-if="earnedDiscount.systemKeyword === 'PercentageOffItem'"
        >
          <AoeCartDetailSelectPercentageOffItem
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :GetCartItemsByPoeDiscPlanLevelDiscId="
              GetCartItemsByPoeDiscPlanLevelDiscId
            "
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
            @update-cart="updateCartHandler($event)"
          >
          </AoeCartDetailSelectPercentageOffItem>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'Coupon'">
          <AoeCartDetailCouponAward :earnedDiscount="earnedDiscount">
          </AoeCartDetailCouponAward>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'FreeDollars'">
          <AoeCartDetailFreeDollars
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            @update-cart="updateCartHandler($event)"
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
          >
          </AoeCartDetailFreeDollars>
        </template>
        <template
          v-else-if="earnedDiscount.systemKeyword === 'DiscountedSkuFromList'"
        >
          <AoeCartDetailDiscountedSkuFromList
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :GetCartItemsByPoeDiscPlanLevelDiscId="
              GetCartItemsByPoeDiscPlanLevelDiscId
            "
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
            @update-cart="updateCartHandler($event)"
          >
          </AoeCartDetailDiscountedSkuFromList>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'DiscountedItem'">
          <AoeCartDetailDiscountedItem
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
            @update-cart="updateCartHandler($event)"
          ></AoeCartDetailDiscountedItem>
        </template>
        <div class="col-12" v-else>
          {{ t('cart.SystemKeyword') }} {{ earnedDiscount.systemKeyword }} {{ t('cart.NotSupported') }}
        </div>

        <!-- Divider between discount awards -->
        <div v-if="index < FilterEarnedDisounts(props.cart.earnedDiscounts!).length - 1" class="col-12 mt-3">
          <hr class="discount-divider" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.aoe-discount-control {
  .discount-divider {
    border: 0;
    border-top: 1px solid #e0e0e0;
    margin: 0;
  }

  .unselect-select-free-product {
    text-align: right;
  }

  .select-free-product {
    cursor: pointer;
    text-align: center;
    padding: $spacing-1;
    @include typography-body;
    @include font-weight-regular;
    font-style: normal;
  }

  .prodImage {
    max-height: 50px;
    max-width: 50px;
    margin: auto;
  }

  .selectedLabelDiv {
    text-align: left;
  }

  .checkMark {
    color: var(--standardColor) !important;
  }

  .freeItemOptions {
    display: table;
    border-spacing: 20px;
    margin-top: -20px;
    margin-bottom: -20px;
    margin-left: auto;
    margin-right: auto;
  }

  .freeOption {
    display: table-cell;
  }

  .freeOption.selected {
    border-color: var(--standardColor) !important;
  }

  .discountMessage {
    @include typography-h4;
    @include font-weight-medium;
    font-style: normal;
    align-content: center;
  }

  .percent-off-item-entry-div .single-line-auto-complete-row {
    margin-top: 0rem !important;
  }

  .percent-off-item-entry-div .single-line-auto-complete-div {
    line-height: 34px;
    align-content: center;
  }

  .percent-off-item-entry-div .single-line-add-to-cart-button {
    line-height: 34px;
    align-content: center;
    padding: 0 $spacing-4 !important;
  }

  .unselect-select-free-product {
    @include typography-small;
    @include font-weight-medium;
    font-style: normal;
  }

  .unselect-select-free-product .k-button-text {
    color: var(--buttonPrimaryColor);
    text-wrap-mode: wrap;
  }

  .unselect-select-free-product .k-button-link {
    text-decoration: underline;
  }

  .notSelected {
    color: white;
  }

  @media screen and (max-width: 768px) {
    .freeItemOptions {
      display: table;
      border-spacing: 10px;
      margin-top: -10px;
      margin-bottom: 10px;
      margin-left: auto;
      margin-right: auto;
    }
    .selectedLabelDiv {
      text-align: left;
      @include typography-caption;

    }
  }

  .percent-off-order-award-desc,
  .free-shipping-award-desc {
    @include typography-h4;
  }

  .check-mark-outline-icon {
    width: 24px;
    height: 24px;
    color: var(--standardColor);
  }
}

.gamificationMessage {
  @include typography-body-large;
}

.gamificationRange {
  width: 100% !important;
  background: #f0f0f0 !important;
}
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  input[type='range'] {
    overflow: hidden;
    width: 80px;
    -webkit-appearance: none;
    appearance: none;
    background-color: #9a905d;
  }

  input[type='range']::-webkit-slider-thumb {
    width: 10px;
    -webkit-appearance: none;
    height: 10px;
    cursor: ew-resize;
    background: var(--standardColor);
    box-shadow: -1000px 0 0 1000px var(--standardColor);
  }
}
</style>
